import './submission-status-badge.scss';

import {
  Box,
  Stack,
  SvgIconTypeMap,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Cancel,
  Check,
  MoreHoriz,
  PriorityHigh,
  QuestionMark,
  Redo,
  Sync,
} from '@mui/icons-material';
import {
  ClaimStatus,
  Exact,
  GetStatusesQueryStatus,
  InputMaybe,
  PaginationRequest,
  PaginationSort,
  Provider,
  StatusDagIdEnum,
  StatusFilterInput,
  StatusTypeEnum,
  StatusValue,
  SubmissionStatus,
  SubmissionSubStatus,
  TargetAreaType,
  providerToDisplay,
  statusValueToStatus,
  useStatusesQuery,
} from '@converge/graphql';
import {
  ConvergeButton,
  ConvergeTooltip,
  DialogLayout,
  StatusBadge,
  StyledConvergeFormTableCell,
  StyledConvergeFormTableContainer,
  useAsCase,
} from '../../presentation';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  asCase,
  displayDateFromNow,
  displayFormatDate,
  formatDurationBetween,
} from '@converge/utils';

import { CustomPaletteColor } from '../../theme';
import config from '@converge/config';
import { useAuthContext } from '../auth/require-auth';
import { sentenceCase } from 'change-case';

interface IProps {
  status: SubmissionStatus | null | undefined | ClaimStatus;
  subStatus?: SubmissionSubStatus | null | undefined;
  uuid: string;
  createdAt: string;
}

export const SubmissionStatusBadge = ({
  status,
  uuid,
  createdAt,
  subStatus,
}: IProps) => {
  const [show, setShow] = useState<boolean>(false);
  const { isUserIdle } = useAuthContext();

  const [shouldPoll, setShouldPoll] = useState<boolean>(false);

  const { info } = useStatusesQuery({
    operationName: 'submissionStatusBadge',
    filters: {
      targetUuid: uuid,
      targetArea: TargetAreaType.Submission,
      details: false,
      statusType: StatusTypeEnum.DataCollection,
    },
    skip: !config.featureFlags.enableDiagnosticMode,
    poll: isUserIdle ? false : shouldPoll,
  });

  useEffect(() => {
    if (info.shouldPoll !== shouldPoll) {
      setShouldPoll(info.shouldPoll);
    }
  }, [info.shouldPoll, shouldPoll]);

  const color = useMemo(() => {
    switch (status) {
      case SubmissionStatus.Referred:
      case SubmissionStatus.Indicated:
      case SubmissionStatus.Hold:
      case SubmissionStatus.RequestedMoreInfo:
        return '#EF6C00;'; // Yellow/Warning color
      case SubmissionStatus.Closed:
      case SubmissionStatus.Declined:
      case SubmissionStatus.Lost:
      case SubmissionStatus.Cancelled:
      case SubmissionStatus.PendingCancellation:
        return '#D32F2F'; // Red/Error color
      case SubmissionStatus.Quoted:
      case SubmissionStatus.Submitted:
      case SubmissionStatus.Cleared:
      case SubmissionStatus.Bound:
      case SubmissionStatus.Evaluation:
        return '#2e7d32'; // Darker green for Bound
      case SubmissionStatus.PolicyIssued:
        return '#0288D1'; // Blue for Policy Issued

      default:
        return '#2e7d32'; // Green/Success color
    }
  }, [status]);

  const backgroundColor = useMemo(() => {
    switch (status) {
      case SubmissionStatus.Referred:
      case SubmissionStatus.Indicated:
      case SubmissionStatus.Hold:
      case SubmissionStatus.RequestedMoreInfo:
        return '#FFF4E5;'; // Yellow/Warning color
      case SubmissionStatus.Closed:
      case SubmissionStatus.Declined:
      case SubmissionStatus.Lost:
      case SubmissionStatus.Cancelled:
      case SubmissionStatus.PendingCancellation:
        return '#FDEDED'; // Red/Error color
      case SubmissionStatus.Quoted:
      case SubmissionStatus.Submitted:
      case SubmissionStatus.Cleared:
      case SubmissionStatus.Bound:
      case SubmissionStatus.Evaluation:
        return '#EDF7ED'; // Darker green for Bound
      case SubmissionStatus.PolicyIssued:
        return '#E5F6FD;'; // Blue for Policy Issued

      default:
        return '#EDF7ED'; // Green/Success color
    }
  }, [status]);

  const tooltip = useMemo(() => {
    // Special case for RequestedMoreInfo
    if (status === SubmissionStatus.RequestedMoreInfo) {
      return 'Requested More Information';
    }
    // Special case for PendingCancellation
    if (status === SubmissionStatus.PendingCancellation) {
      return 'Pending Cancellation';
    }

    const tooltipText = [
      status,
      subStatus ? subStatus.replace(new RegExp(`^${status}_`), '') : undefined,
    ]
      .filter(Boolean)
      .map((str) => asCase(str, asCase.StringCase.Sentence))
      .join(' - ');

    return tooltipText;
  }, [status, subStatus]);

  const displayLabel = useMemo(() => {
    if (status === SubmissionStatus.RequestedMoreInfo) {
      return 'REQUESTED...';
    }
    if (status === SubmissionStatus.PendingCancellation) {
      return 'PENDING CAN...';
    }

    // Show all status labels in uppercase
    return status ? sentenceCase(status) : '';
  }, [status]);

  const shouldShowTooltip = useMemo(() => {
    // Show tooltip for RequestedMoreInfo and PendingCancellation
    return (
      (status === SubmissionStatus.RequestedMoreInfo ||
        status === SubmissionStatus.PendingCancellation) &&
      Boolean(tooltip)
    );
  }, [status, tooltip]);

  const id = `status-badge-popover-${uuid}`;

  return (
    <>
      {config.featureFlags.enableDiagnosticMode && (
        <DialogLayout
          fullWidth
          showCloseButton
          onCloseIconClick={() => {
            setShow(false);
          }}
          maxWidth={false}
          onClose={() => {
            setShow(false);
          }}
          open={show}
          header={`Submission status diagnosis for ${uuid}`}
          // open={open}
          // anchorEl={anchorEl}
          id={id}
          // sx={{
          //   pointerEvents: 'none',
          // }}
          // onClose={() => {
          //   setAnchorEl(null);
          // }}
          // disableRestoreFocus
          // anchorOrigin={{
          //   vertical: 'bottom',
          //   horizontal: 'left',
          // }}
          // transformOrigin={{
          //   vertical: 'top',
          //   horizontal: 'left',
          // }}
        >
          <Box>
            <DiagnositicsStatusesList uuid={uuid} createdAt={createdAt} />
          </Box>
        </DialogLayout>
      )}
      <ConvergeTooltip
        title={
          shouldShowTooltip ? <Box textAlign={'center'}>{tooltip}</Box> : ''
        }
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: '#333333',
              color: 'white',
              padding: '10px 16px',
              fontSize: '12px !important',
              textAlign: 'center',
              fontWeight: '600 !important',
            },
          },
          arrow: {
            sx: {
              color: '#333333',
            },
          },
        }}
        arrow
      >
        <span>
          {status && (
            <StatusBadge
              label={displayLabel || ''}
              color={color}
              onClick={
                config.featureFlags.enableDiagnosticMode
                  ? () => {
                      setShow(true);
                    }
                  : undefined
              }
              sx={{
                width: '140px',
                maxWidth:
                  status === SubmissionStatus.PendingCancellation
                    ? '140px'
                    : '100%',

                fontSize: '12px !important',
                fontWeight: '500 !important',
                padding:
                  status === SubmissionStatus.RequestedMoreInfo ||
                  status === SubmissionStatus.PendingCancellation
                    ? '4px 2px'
                    : '4px 4px',
                letterSpacing:
                  status === SubmissionStatus.RequestedMoreInfo ||
                  status === SubmissionStatus.PendingCancellation
                    ? '0.3px !important'
                    : '0.5px !important',
                borderRadius: '12px !important',
                textTransform: 'uppercase !important',
                backgroundColor: `${backgroundColor}`,

                whiteSpace: 'nowrap',
                '& .MuiChip-label': {
                  padding:
                    status === SubmissionStatus.RequestedMoreInfo ||
                    status === SubmissionStatus.PendingCancellation
                      ? '4px 2px'
                      : '4px 4px',
                  fontSize: '12px !important',
                  fontWeight: '500 !important',
                  textTransform: 'uppercase !important',
                  letterSpacing:
                    status === SubmissionStatus.RequestedMoreInfo ||
                    status === SubmissionStatus.PendingCancellation
                      ? '0.3px !important'
                      : '0.5px !important',

                  whiteSpace: 'nowrap',
                },
              }}
            />
          )}
        </span>
      </ConvergeTooltip>
    </>
  );
};

interface StatusListProps {
  statuses?: GetStatusesQueryStatus[];
  view?: 'diagnosis' | 'data-collection';
  statusDagId?: StatusDagIdEnum;
  mode?: 'fixed' | 'dynamic';
  statusAreaLabel?: string;
  filter?: (status: GetStatusesQueryStatus) => boolean;
  poll: boolean;
  showDagIdColumn?: boolean;
  refetch?: (
    variables?:
      | Partial<
          Exact<{
            filters: StatusFilterInput;
            sort: PaginationSort;
            pagination?: InputMaybe<PaginationRequest> | undefined;
          }>
        >
      | undefined
  ) => Promise<unknown>;
}

export const StatusesList = ({
  statuses = [],
  view = 'data-collection',
  statusDagId,
  mode = 'dynamic',
  filter,
  statusAreaLabel = 'Data source',
  poll,
  showDagIdColumn,
  refetch,
}: StatusListProps) => {
  const {
    filteredStatuses,
    dnbLatestStatus,
    relativity6LatestStatus,
    sscLatestStatus,
    dnbStatus = dnbLatestStatus,
    relativity6Status = relativity6LatestStatus,
    sscStatus = sscLatestStatus,
  } = useMemo<
    Record<
      | 'dnbLatestStatus'
      | 'relativity6LatestStatus'
      | 'constellaLatestStatus'
      | 'sscLatestStatus'
      | 'dnbStatus'
      | 'relativity6Status'
      | 'constellaStatus'
      | 'sscStatus',
      GetStatusesQueryStatus | undefined | null
    > & {
      filteredStatuses: GetStatusesQueryStatus[];
    }
  >(() => {
    const filteredStatuses = statuses.filter(
      (status) =>
        (filter ? filter(status) : true) &&
        (statusDagId ? status.statusDagId === statusDagId : true)
    );
    return {
      filteredStatuses,
      dnbLatestStatus: filteredStatuses.find((status) =>
        status.statusArea === 'Dun & Bradstreet'
          ? true
          : status.statusKey?.includes(':') &&
            status.statusKey.startsWith('dnb')
      ),
      relativity6LatestStatus: filteredStatuses.find((status) =>
        status.statusArea === 'Relativity6'
          ? true
          : status.statusKey?.includes(':') &&
            status.statusKey.startsWith('relativity6')
      ),
      constellaLatestStatus: filteredStatuses.find((status) =>
        status.statusArea === 'Constella'
          ? true
          : status.statusKey?.includes(':') &&
            status.statusKey.startsWith('constella')
      ),
      sscLatestStatus: filteredStatuses.find((status) =>
        typeof status.statusArea === 'string' &&
        status.statusArea.replace(/ /g, '')
          ? true
          : status.statusKey?.includes(':') &&
            status.statusKey.startsWith('ssc')
      ),
      dnbStatus: filteredStatuses.find(
        (status) => status.statusKey === 'dnb:etl_status'
      ),
      relativity6Status: filteredStatuses.find(
        (status) => status.statusKey === 'relativity6:etl_status'
      ),
      constellaStatus: filteredStatuses.find(
        (status) => status.statusKey === 'constella:etl_status'
      ),
      sscStatus: filteredStatuses.find(
        (status) => status.statusKey === 'ssc:etl_status'
      ),
    };
  }, [filter, statusDagId, statuses]);

  const viewProvidersAs: 'row' | 'column' = 'row' as 'row' | 'column';

  return (
    <StyledConvergeFormTableContainer>
      <Table sx={{ minWidth: 650 }} aria-label="status" size="small">
        <TableHead>
          {viewProvidersAs === 'row' && (
            <TableRow>
              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                className="table-header-overview-page"
              >
                Stage
              </StyledConvergeFormTableCell>

              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                className="table-header-overview-page"
              >
                {statusAreaLabel}
              </StyledConvergeFormTableCell>
              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                className="table-header-overview-page"
              >
                Run date
              </StyledConvergeFormTableCell>
              <StyledConvergeFormTableCell
                component="th"
                scope="row"
                className="table-header-overview-page"
              >
                Status
              </StyledConvergeFormTableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {mode === 'dynamic' ? (
            filteredStatuses.length > 0 ? (
              filteredStatuses.map((status) => (
                <StatusRow
                  key={status.uuid}
                  status={status}
                  provider={status.statusArea}
                  showDagIdColumn={showDagIdColumn}
                />
              ))
            ) : (
              <TableRow>
                <StyledConvergeFormTableCell
                  component="th"
                  scope="row"
                  colSpan={3}
                >
                  <Typography color="gray">No statuses found</Typography>
                </StyledConvergeFormTableCell>
              </TableRow>
            )
          ) : (
            <>
              <StatusRow provider={Provider.Dnb} status={dnbStatus} />
              <StatusRow
                provider={Provider.Relativity6}
                status={relativity6Status}
              />
              <StatusRow
                provider={Provider.SecurityScorecard}
                status={sscStatus}
              />
            </>
          )}
        </TableBody>
      </Table>
    </StyledConvergeFormTableContainer>
  );
};

export const DiagnositicsStatusesList = ({
  uuid,
  createdAt,
}: {
  uuid: string;
  createdAt: string;
  title?: React.ReactNode;
}) => {
  const [filterDetails, setFilterDetails] = useState<boolean | null>(null);

  const {
    info: { statuses, totalResults, shouldPoll },
  } = useStatusesQuery({
    operationName: 'diagnosticsStatusesList',
    skip: !config.featureFlags.enableDiagnosticMode,
    filters: {
      targetUuid: uuid,
      targetArea: TargetAreaType.Submission,
      details: filterDetails ?? undefined,
    },
  });

  const nextFilterDetailValue = useMemo(() => {
    if (filterDetails === null) {
      return true;
    }
    if (filterDetails === true) {
      return false;
    }
    return null;
  }, [filterDetails]);

  const updateFilterDetails = useCallback(() => {
    setFilterDetails(nextFilterDetailValue);
  }, [nextFilterDetailValue]);

  return (
    <>
      <Stack flexDirection={'row'} alignItems={'center'}>
        <Stack>
          <Typography padding={2} variant="h5">
            {typeof totalResults === 'number' ? (
              <>
                {totalResults} submission status
                {totalResults > 0 ? 'es' : ''}
              </>
            ) : (
              <>Submission status</>
            )}
            {shouldPoll && ' (polling)'}
          </Typography>
        </Stack>
        <Box flexGrow={1} />
        <Stack gap={2} flexDirection={'row'}>
          <ConvergeButton
            label={'Show details'}
            sx={{
              textDecoration: filterDetails === true ? 'underline' : 'none',
            }}
            disabled={nextFilterDetailValue !== true}
            onClick={updateFilterDetails}
          />
          <ConvergeButton
            label={'Hide details'}
            sx={{
              textDecoration: filterDetails === false ? 'underline' : 'none',
            }}
            disabled={nextFilterDetailValue !== false}
            onClick={updateFilterDetails}
          />
          <ConvergeButton
            label={'Show all'}
            sx={{
              textDecoration: filterDetails === null ? 'underline' : 'none',
            }}
            disabled={nextFilterDetailValue !== null}
            onClick={updateFilterDetails}
          />
        </Stack>
      </Stack>

      {/* TODO: */}
      {/* <Box sx={{ maxWidth: 300, padding: 2 }}>
        <FormTriStateSwitchField
          name={'details'}
          label={'Show details'}
          value={filterDetails}
          onChange={({ currentTarget }) => {
            setFilterDetails(currentTarget.checked);
          }}
        />
      </Box> */}
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="status" size="small">
          <TableHead>
            <TableRow sx={{ fontWeight: 'bold' }}>
              <TableCell component="th" scope="row" width="60px">
                Update #
              </TableCell>
              <TableCell component="th" scope="row">
                DAG
              </TableCell>
              <TableCell component="th" scope="row">
                Key
              </TableCell>
              <TableCell component="th" scope="row">
                Area
              </TableCell>
              <TableCell component="th" scope="row">
                Name
              </TableCell>
              <TableCell component="th" scope="row">
                Status
              </TableCell>
              <TableCell component="th" scope="row">
                Type
              </TableCell>
              <TableCell component="th" scope="row">
                Duration
              </TableCell>
              <TableCell component="th" scope="row">
                Updated
              </TableCell>
              <TableCell component="th" scope="row">
                Created
              </TableCell>
              <TableCell component="th" scope="row">
                User
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {statuses.map((status, index) => {
              const previousStatus = statuses[index + 1];
              return (
                <TableRow
                  key={status.uuid}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell># {statuses.length - index}</TableCell>
                  <TableCell>{status.statusDagId}</TableCell>
                  <TableCell>{status.statusKey}</TableCell>
                  <TableCell>{status.statusArea}</TableCell>
                  <TableCell>{status.statusName}</TableCell>
                  <TableCell>{status.statusValue}</TableCell>
                  <TableCell>{status.statusType}</TableCell>
                  <TableCell>
                    {previousStatus ? (
                      formatDurationBetween(
                        status.updatedAt,
                        previousStatus.updatedAt
                      )
                    ) : (
                      <>&mdash;</>
                    )}
                  </TableCell>
                  <TableCell>
                    <ConvergeTooltip
                      title={displayFormatDate(
                        status.updatedAt,
                        displayFormatDate.DateFormatType.USADateFormatWithTimeAt
                      )}
                    >
                      <span>
                        {displayDateFromNow(status.updatedAt, {
                          ifOlderThanShowDate: true,
                          showTime: true,
                        })}
                      </span>
                    </ConvergeTooltip>
                  </TableCell>
                  <TableCell>
                    <ConvergeTooltip
                      title={displayFormatDate(
                        status.createdAt,
                        displayFormatDate.DateFormatType.USADateFormatWithTimeAt
                      )}
                    >
                      <span>
                        {displayDateFromNow(status.createdAt, {
                          ifOlderThanShowDate: true,
                          showTime: true,
                        })}
                      </span>
                    </ConvergeTooltip>
                  </TableCell>
                  <TableCell>{status.user?.node?.username}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const StatusRow = ({
  provider,
  status,
  randomStatus,
  showDagIdColumn,
}: {
  provider: Provider | string | null | undefined;
  status?: GetStatusesQueryStatus | null;
  randomStatus?: boolean;
  showDagIdColumn?: boolean;
}) => {
  if (randomStatus && status) {
    const randomNumber = Math.ceil(Math.random() * 5);
    status = {
      ...status,
      statusValue:
        randomNumber === 1
          ? 'completed'
          : randomNumber === 2
          ? 'failed'
          : randomNumber === 3
          ? 'pending'
          : randomNumber === 4
          ? 'foo bar'
          : 'in-progress',
    };
  }

  const statusValue = statusValueToStatus(status?.statusValue);

  // const statusAsSentenceCase = useAsCase({
  //   value: statusValue,
  //   casing: 'sentence',
  // });

  const updateStatusDagId =
    status?.statusDagId === 'ENTITY_ETL'
      ? 'Entity Update'
      : status?.statusDagId;

  const statusDagIdAsSentenceCase = useAsCase({
    value: updateStatusDagId ?? '',
    casing: 'title',
    transformAfter: (str) =>
      typeof str === 'string' ? str.replace(/ And /gm, ' & ') : str,
  });

  return (
    <TableRow>
      {showDagIdColumn && (
        <StyledConvergeFormTableCell component="th" scope="row">
          {statusDagIdAsSentenceCase}
        </StyledConvergeFormTableCell>
      )}
      <StyledConvergeFormTableCell>
        {providerToDisplay(provider)}
      </StyledConvergeFormTableCell>
      <StyledConvergeFormTableCell>
        <ConvergeTooltip
          title={
            status ? (
              status?.updatedAt === status?.createdAt ? (
                displayFormatDate(
                  status?.createdAt,
                  displayFormatDate.DateFormatType
                    .USADateFormatWithTimeAndSecondsAt
                )
              ) : (
                <>
                  {displayFormatDate(
                    status?.createdAt,
                    displayFormatDate.DateFormatType
                      .USADateFormatWithTimeAndSecondsAt
                  )}{' '}
                  to{' '}
                  {displayFormatDate(
                    status?.updatedAt,
                    displayFormatDate.DateFormatType
                      .USADateFormatWithTimeAndSecondsAt
                  )}{' '}
                  ({formatDurationBetween(status?.updatedAt, status?.createdAt)}
                  )
                </>
              )
            ) : (
              'Not available'
            )
          }
        >
          <span>
            {status
              ? displayDateFromNow(status.updatedAt, {
                  ifOlderThanShowDate: true,
                  showTime: true,
                })
              : 'N/A'}
          </span>
        </ConvergeTooltip>
      </StyledConvergeFormTableCell>
      <StyledConvergeFormTableCell width="110px">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <div>
            <StatusBadge
              color={statusValueToColor(statusValue)}
              disableCasing={statusValue === StatusValue.Other}
              label={
                !status?.statusValue
                  ? 'N/A'
                  : statusValue === StatusValue.Other
                  ? status?.statusValue
                  : statusValue
              }
              icon={<StatusIcon status={statusValue} />}
            />
          </div>
        </Box>
      </StyledConvergeFormTableCell>
    </TableRow>
  );
};

const StatusIcon = ({
  status,
  color = statusValueToColor(status) as SvgIconTypeMap['props']['color'],
}: {
  status: StatusValue;
  color?: SvgIconTypeMap['props']['color'];
}) => {
  const iconProps: SvgIconTypeMap['props'] = {
    color,
    fontSize: 'small',
    sx: {
      padding: '3px',
      display: 'flex',
      marginRight: '-5px',
      paddingLeft: '5px',
    },
  };
  let icon: ReactNode;

  switch (status) {
    case StatusValue.Cancelled:
      icon = <Cancel {...iconProps} />;
      break;
    case StatusValue.Skipped:
      icon = <Redo {...iconProps} />;
      break;
    case StatusValue.Completed:
      icon = <Check {...iconProps} />;
      break;
    case StatusValue.Failed:
      icon = <PriorityHigh {...iconProps} />;
      break;
    case StatusValue.InProgress:
      icon = <Sync className="spin" {...iconProps} />;
      break;
    case StatusValue.Pending:
      icon = <MoreHoriz {...iconProps} />;
      break;
    default:
      icon = <QuestionMark {...iconProps} />;
      break;
  }

  return icon;
};

export const statusValueToColor = (
  statusValue: StatusValue | undefined | null
): CustomPaletteColor => {
  switch (statusValue) {
    case StatusValue.Completed:
      return CustomPaletteColor.Success;
    case StatusValue.Failed:
      return CustomPaletteColor.Error;
    case StatusValue.InProgress:
      return CustomPaletteColor.Blue;
    case StatusValue.InProcess:
      return statusValueToColor(StatusValue.InProgress);
    case StatusValue.Pending:
      return CustomPaletteColor.DarkGray;
    default:
      return CustomPaletteColor.Primary;
  }
};
